/**
 * NotificationToast
 *
 * A live notification, announced while the app is open. It is a preview of the
 * row waiting in the inbox, so it is built from the same parts in the same
 * order — the notification's avatar (the person, or the kind's icon), the
 * API's title, its message, its one action.
 *
 * `ToastShell` is everything else: the popover surface, the clock, dismiss and
 * the leading rail — the inbox's unread rail for an ordinary arrival, a
 * semantic colour only when the notification carries a state (done, needs
 * attention, failed).
 */

import { useEffect, useRef, useState } from 'react';

import { NotificationUtils } from '@personal-trainer-app/shared';

import type { NotificationToastProps } from './NotificationToast.types';
import type { ToastShellVariant } from '@/components/ui';
import type {
  NotificationCategory,
  NotificationLevel,
  NotificationResponseOption,
} from '@personal-trainer-app/shared';

import {
  NotificationAvatar,
  getNotificationPresentation,
} from '@/components/notifications';
import { Button, SectionLabel, ToastShell } from '@/components/ui';

/**
 * Level → shell variant. `info` is not "blue for information": an ordinary
 * arrival has no state to report, so it asks for the inbox's unread rail by
 * name. Semantic colour is kept for the levels that mean something.
 */
const LEVEL_VARIANTS: Record<NotificationLevel, ToastShellVariant> = {
  info: 'accent',
  success: 'success',
  warning: 'warning',
  error: 'error',
};

const CATEGORY_LABELS: Record<NotificationCategory, string> = {
  achievement: 'Achievement',
  challenge: 'Challenge',
  social: 'Social',
  coaching: 'Coaching',
  report: 'Report',
  training: 'Training',
  account: 'Account',
};

export function NotificationToast({
  notification,
  duration,
  onAction,
  onRemove,
}: NotificationToastProps): React.JSX.Element {
  const { title, message, level, category } =
    NotificationUtils.getEnvelope(notification);
  const { respond, primaryAction } = getNotificationPresentation(notification);

  // The clock holds while an action is in flight.
  const [busy, setBusy] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    // Re-armed on mount: StrictMode runs this cleanup once before the real one.
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return (
    <ToastShell
      variant={LEVEL_VARIANTS[level]}
      duration={duration}
      paused={busy}
      onRemove={onRemove}
      data-testid="notification-toast"
    >
      {({ dismiss }) => {
        const handleAction = async (
          option?: NotificationResponseOption
        ): Promise<void> => {
          setBusy(true);
          const done = await onAction(notification, option);
          // Dismissed while the action was in flight: the toast has gone, and
          // the action landed without it.
          if (!mountedRef.current) return;
          setBusy(false);
          // A failure keeps the toast: its error is shown, and the way to
          // retry is the button that is still here.
          if (done) dismiss();
        };

        return (
          <>
            <NotificationAvatar notification={notification} />

            <div className="min-w-0 flex-1">
              <SectionLabel as="p">
                {CATEGORY_LABELS[category] ?? 'Notification'}
              </SectionLabel>
              <p className="mt-0.5 text-body-md font-medium leading-snug text-neutral-50">
                {title}
              </p>
              {message && (
                <p className="mt-0.5 line-clamp-2 text-body-sm text-neutral-400">
                  {message}
                </p>
              )}

              {(respond || primaryAction) && (
                <div className="mt-2.5 flex items-center gap-2">
                  {respond ? (
                    respond.options.map((option) => (
                      // Accept is the one primary action, so sakura — never
                      // semantic green; declining is neutral, not an error.
                      <Button
                        key={option.intent}
                        variant={
                          option.intent === 'accept'
                            ? 'outline-primary'
                            : 'ghost'
                        }
                        size="xs"
                        disabled={busy}
                        onClick={() => void handleAction(option)}
                      >
                        {option.label}
                      </Button>
                    ))
                  ) : (
                    // Bordered, like the standard toast's action: a button
                    // before anyone hovers it.
                    <Button
                      variant="outline-primary"
                      size="xs"
                      loading={busy}
                      onClick={() => void handleAction()}
                    >
                      {primaryAction?.label}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </>
        );
      }}
    </ToastShell>
  );
}

export default NotificationToast;
